import ButtonGroup from 'common/components/ButtonGroup/ButtonGroup';
import DateInput from 'common/components/DateInput/DateInput';
import PhoneInput from 'common/components/PhoneInput/PhoneInput';
import DebouncedInput from 'common/components/DebouncedInput/DebouncedInput';
import Select from 'common/components/Select/Select';
import Text from 'common/components/Text/Text';
import TimePicker from 'common/components/TimePicker/TimePicker';
import useTranslation from 'common/contexts/translations';
import getFF from 'common/utils/getFF';
import PropTypes from 'prop-types';
import React from 'react';
import { Field } from 'react-final-form';
import { StyleSheet, Switch, View } from 'react-native';
import useSite from 'sites/contexts/sites';
import theme from 'styles';

const Input = ({
  type, name, placeholder, errorMessage, children, label,
  labelStyle, containerStyle, inputStyle, inputContainerStyle,
  value, onChange, meta,
  ...otherProps
}) => {
  const { lang } = useTranslation();
  const { item: site } = useSite();

  switch (type) {
    case 'date':
      return (
        <DateInput
          value={value}
          onChange={onChange}
          label={name}
          placeholder={placeholder}
          errorStyle={{ color: theme.colors.danger }}
          errorMessage={errorMessage}
          {...otherProps}
        />
      );
    case 'phone':
      return (
        <PhoneInput
          value={value}
          onChange={onChange}
          label={label}
          placeholder={placeholder}
          errorStyle={{ color: theme.colors.danger }}
          errorMessage={errorMessage}
          {...otherProps}
        />
      );
    case 'switch':
      return (
        <View style={[styles(lang).horizontal, styles(lang).containerStyle]}>
          <Text
            isBold
            style={styles(lang).switchLabel}
            customColor={site?.template?.backdropTextColor || theme.colors.dark}
          >
            {label}

          </Text>
          <Switch
            onValueChange={onChange}
            value={value}
          />
        </View>
      );
    case 'select':
      return (
        <View style={styles(lang).containerStyle}>
          <Text
            isBold
            style={styles(lang).label}
            customColor={site?.template?.backdropTextColor || theme.colors.dark}
          >
            {label}

          </Text>
          <Select
            {...otherProps}
            onChange={onChange}
            value={value}
          />
        </View>
      );
    case 'time':
      return (
        <View style={styles(lang).containerStyle}>
          <Text
            isBold
            style={styles(lang).label}
            customColor={site?.template?.backdropTextColor || theme.colors.dark}
          >
            {label}

          </Text>
          <TimePicker
            {...otherProps}
            onChange={onChange}
            value={value}
          />
        </View>
      );
    case 'buttongroup':
      return (
        <View style={styles(lang).containerStyle}>
          <Text
            isBold
            style={styles(lang).label}
            customColor={site?.template?.backdropTextColor || theme.colors.dark}
          >
            {label}

          </Text>
          <ButtonGroup
            {...otherProps}
            onChange={onChange}
            value={value}
          />
        </View>
      );
    case 'custom':
      return (
        <View style={styles(lang).containerStyle}>
          <Text
            isBold
            style={styles(lang).label}
            customColor={site?.template?.backdropTextColor || theme.colors.dark}
          >
            {label}

          </Text>
          {React.cloneElement(children, { ...otherProps, value, onChange })}
        </View>
      );

    default:
      let keyboardType = 'default';

      switch (type) {
        case 'number': keyboardType = 'number-pad'; break;
        case 'phone': keyboardType = 'phone-pad '; break;
        case 'email': keyboardType = 'email-address'; break;
        case 'decimal': keyboardType = 'decimal-pad'; break;
        default: break;
      }

      return (
        <>
          <DebouncedInput
            value={value}
            secureTextEntry={type === 'password'}
            keyboardType={keyboardType}
            type={type}
            autoCapitalize={type === 'code' ? 'characters' : 'none'}
            onChange={onChange}
            label={label}
            placeholder={placeholder}
            errorStyle={{ color: theme.colors.danger }}
            errorMessage={errorMessage}
            inputStyle={{
              ...inputStyle,
              fontSize: theme.fontSizes.medium,
              maxWidth: '100%',
              color: site?.template?.backdropTextColor || theme.colors.dark,
            }}
            inputContainerStyle={{
              borderBottomColor: site?.template?.primaryColor || theme.colors.primary,
              ...inputContainerStyle,
            }}
            containerStyle={{
              marginVertical: theme.sizings.small,
              ...containerStyle,
            }}
            labelStyle={{
              // textTransform: 'capitalize',
              // fontFamily: getFF(lang, true),
              color: site?.template?.backdropTextColor || theme.colors.dark,
              fontWeight: 'bold',
              fontSize: theme.fontSizes.medium,
              fontFamily: site?.template?.primaryFont || theme.fonts.bold,
              ...labelStyle,
            }}
          />
          {meta && meta.touched && meta.error && <Text>{meta.error}</Text>}
        </>

      );
  }
};

Input.propTypes = {
  meta: PropTypes.any,
  value: PropTypes.any,
  onChange: PropTypes.func,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
  errorMessage: PropTypes.string,
  children: PropTypes.element,
  label: PropTypes.string,
  type: PropTypes.string,
  inputStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  inputContainerStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  containerStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  labelStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
};

Input.defaultProps = {
  meta: null,
  value: '',
  onChange: () => { },
  type: 'text',
  name: 'input',
  icon: '',
  placeholder: '',
  disabled: false,
  options: [],
  errorMessage: '',
  children: null,
  label: '',
  inputStyle: 0,
  inputContainerStyle: 0,
  containerStyle: 0,
  labelStyle: 0,
};

const FormInput = ({
  name,
  finalForm,
  required,
  validate,
  type,
  ...rest
}) => {
  const { t } = useTranslation();
  const reqRule = (value) => (value !== '' && value != null ? undefined : t('common.fieldRequired'));
  const checkboxReq = (value) => value === true ? undefined : t('common.fieldRequired');

  let validator = !validate && required ? reqRule : validate;

  validator = (type === 'checkbox' && required) ? checkboxReq : validator;

  if (finalForm === false) {
    return <Input name={name} type={type} {...rest} />;
  }
  return (
    <Field
      name={name}
      validate={validator}
      required={required}
      render={({ input, meta }) => (
        <Input {...rest} value={input.value} onChange={input.onChange} meta={meta} />
      )}

    />
  );
};

const styles = (lang) => StyleSheet.create({
  inputContainerStyle: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.primary,
  },
  label: {
    paddingBottom: theme.sizings.small,
    fontSize: theme.fontSizes.medium,
    fontFamily: getFF(lang, true),
  },
  containerStyle: {
    marginVertical: theme.sizings.small,
    paddingHorizontal: theme.sizings.small,
  },
  horizontal: {
    flexDirection: 'row',
  },

  switchLabel: {
    marginRight: theme.sizings.small,
    fontFamily: getFF(lang, true),
  },
});

FormInput.propTypes = {
  finalForm: PropTypes.bool,
  inline: PropTypes.bool,
  value: PropTypes.any,
  onChange: PropTypes.func,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  options: PropTypes.array,
  errorMessage: PropTypes.string,
  children: PropTypes.element,
  label: PropTypes.string,
  type: PropTypes.string,
  inputStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  inputContainerStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  containerStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  labelStyle: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.array]),
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
  required: PropTypes.bool,
};

FormInput.defaultProps = {
  finalForm: true,
  inline: false,
  value: '',
  onChange: () => { },
  type: 'text',
  name: 'input',
  icon: '',
  placeholder: '',
  disabled: false,
  options: [],
  errorMessage: '',
  children: null,
  label: '',
  inputStyle: 0,
  inputContainerStyle: 0,
  containerStyle: 0,
  labelStyle: 0,
  validate: null,
  required: false,
};

export default FormInput;
